/**
 * Push notifications (native only).
 *
 * Registers the device with FCM (Android) / APNs (iOS) via
 * @capacitor/push-notifications, sends the token to the backend
 * (POST /api/devices/register) so it can be targeted later, and routes a
 * notification tap to the in-app route carried in its `data.route` payload.
 *
 * Requires native push credentials to actually deliver:
 *   - Android: android/app/google-services.json (Firebase) + FCM enabled
 *   - iOS: an APNs key in the Apple Developer portal + Push capability in Xcode
 * Without them, register() fails and this no-ops gracefully. The backend
 * send path (admin.messaging() → device tokens) is future work.
 */
import { isNative, platform } from '../utils/platform';
import api from '../services/api';
import { navigateTo } from '../services/navigation';

let _registered = false;
let _token = null;

export async function registerPush() {
  if (!isNative || _registered) return;

  let PushNotifications;
  try {
    ({ PushNotifications } = await import('@capacitor/push-notifications'));
  } catch {
    return; // plugin not installed
  }

  try {
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== 'granted') return;

    // Token issued → persist it for this user.
    PushNotifications.addListener('registration', async (token) => {
      _token = token.value;
      try {
        await api.post('/devices/register', { token: token.value, platform });
      } catch {
        // best effort — re-registers on next launch
      }
    });

    PushNotifications.addListener('registrationError', () => {
      // surfaced in native logs; nothing actionable client-side
    });

    // Tap on a notification → deep-link into the app via the route payload.
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const route = action?.notification?.data?.route;
      if (route) navigateTo(route);
    });

    await PushNotifications.register();
    _registered = true;
  } catch {
    // No FCM/APNs config yet → silently skip; app works without push.
  }
}

export default registerPush;

/**
 * Drop this device's registration on sign-out. Without it the phone keeps
 * receiving the previous user's notifications — on a shared or handed-over
 * device that leaks one user's (or one tenant's) content to the next.
 * Best effort: a failure here must never block logout.
 */
export async function unregisterPush() {
  if (!isNative || !_token) return;
  const token = _token;
  _token = null;
  _registered = false;
  try {
    await api.post('/devices/unregister', { token });
  } catch {
    // best effort — the server also drops the row when another user claims it
  }
}
